/**
 * SpendingInsights - Horizontally scrollable insight cards for the home dashboard
 * Shows dynamic insights based on user's financial data
 */
import { useMemo, useRef, useState } from 'react';
import { ChevronRight, Lock } from 'lucide-react';
import { formatCurrency, getCurrentCurrency } from '../../utils/formatters';
import { usePurple } from '../../utils/purple';
import { usePlan } from '../../utils/plan'; // plan_02
import { investmentBalance } from "../../utils/investMath"; // dark_13: one sum for investment balances

// Icon components for different insight types
const PigIcon = () => (
  <span className="text-2xl">🐷</span>
);

const PartyIcon = () => (
  <span className="text-2xl">🎉</span>
);

const HourglassIcon = () => (
  <span className="text-2xl">⏳</span>
);

const ChartUpIcon = () => (
  <span className="text-2xl">📈</span>
);

const ChartDownIcon = () => (
  <span className="text-2xl">📉</span>
);

const TrophyIcon = () => (
  <span className="text-2xl">🏆</span>
);

const SatelliteIcon = () => (
  <span className="text-2xl">📡</span>
);

const LightbulbIcon = () => (
  <span className="text-2xl">💡</span>
);

const WarningIcon = () => (
  <span className="text-2xl">⚠️</span>
);

const FireIcon = () => (
  <span className="text-2xl">🔥</span>
);

const ShoppingIcon = () => (
  <span className="text-2xl">🛍️</span>
);

const ThinkingIcon = () => (
  <span className="text-2xl">💭</span>
);

// Emoji for each insight icon name (used by the Purple list rows)
const INSIGHT_ICONS = {
  pig: <PigIcon />, party: <PartyIcon />, hourglass: <HourglassIcon />, chartUp: <ChartUpIcon />,
  chartDown: <ChartDownIcon />, trophy: <TrophyIcon />, satellite: <SatelliteIcon />, lightbulb: <LightbulbIcon />,
  warning: <WarningIcon />, fire: <FireIcon />, shopping: <ShoppingIcon />, thinking: <ThinkingIcon />,
  clipboard: <span className="text-2xl">📋</span>,
};


// Wishlist names often come straight from a product page ("Buy Apple Watch
// SE 3 - Apple (CA)"). Tidy them enough to sit on a card.
const cleanItemName = (raw) => {
  let n = String(raw || '').trim().replace(/^buy\s+/i, '');
  const head = n.split(/\s+[|\-\u2013\u2014]\s+/)[0];
  if (head && head.length >= 3) n = head;
  if (n.length > 40) n = n.slice(0, 38).trimEnd() + '\u2026';
  return n;
};

// Domain palette — mirrors BudgetHero's tilePalette exactly (Income/Spent/
// Saved-Inv/Leftover, same per-theme hex values) plus one shared warning red,
// so Home shortcuts, Budget tiles, and Insights all read as one color system.
const domainPalette = {
  pink: {
    income:   { icon: '#EC4899', bg: '#FDF1F6', border: '#F6D9E5', text: '#C23A72' },
    spent:    { icon: '#3B82F6', bg: '#EEF4FA', border: '#D7E6F2', text: '#3C6693' },
    saved:    { icon: '#10B981', bg: '#F1F6EC', border: '#DCE9D0', text: '#5C7A4A' },
    leftover: { icon: '#A855F7', bg: '#F4F1FA', border: '#E3DBF2', text: '#7A5FB0' },
    warning:  { icon: '#DC2626', bg: '#FEF2F2', border: '#FECACA', text: '#DC2626' },
  },

  blue: {
    income:   { icon: '#14B8A6', bg: '#EAF6F3', border: '#CDEEE6', text: '#1F6F5C' },
    spent:    { icon: '#6366F1', bg: '#EEEFFB', border: '#DCE3FB', text: '#35399E' },
    saved:    { icon: '#06B6D4', bg: '#EBF8FA', border: '#CFF0F5', text: '#1B6E7D' },
    leftover: { icon: '#F59E0B', bg: '#FBF3E2', border: '#F5E3BE', text: '#93691E' },
    warning:  { icon: '#DC2626', bg: '#FEF2F2', border: '#FECACA', text: '#DC2626' },
  },

  warm: {
    income:   { icon: '#6B9B57', bg: '#F0F3EC', border: '#DCE5D2', text: '#4F6B3F' },
    spent:    { icon: '#B8895A', bg: '#FAF3EA', border: '#EFDDC5', text: '#7C5C3A' },
    saved:    { icon: '#C9971F', bg: '#FBF4E4', border: '#F0DEB0', text: '#8A6220' },
    leftover: { icon: '#B55A3A', bg: '#F8EDE8', border: '#E8C9B8', text: '#8B4A2E' },
    warning:  { icon: '#DC2626', bg: '#FEF2F2', border: '#FECACA', text: '#DC2626' },
  }
};

// Which domain slot each insight type pulls its color from.
// calendar->spent and wishlist->saved are deliberate: they match the Home
// shortcut colors for Calendar and Wishlist exactly. fallback* (generic tip
// cards) stay neutral gray — they're not a domain insight.
const insightTypeSlot = {
  savings: 'saved',
  savingsGoal: 'saved',
  savingsGoalFund: 'saved',
  savingsBehind: 'warning',
  netWorthUp: 'spent',
  netWorthDown: 'warning',
  milestone: 'spent',
  investment: 'leftover',
  investmentReminder: 'warning',
  spendingOver: 'warning',
  spendingUnder: 'income',
  calendar: 'spent',
  wishlist: 'saved',
  thinkItOver: 'saved',
  plannerDue: 'leftover',
};

const getInsightSlot = (theme, type) => {
  const themeKey = theme === 'warm' ? 'warm' : theme === 'blue' ? 'blue' : 'pink';
  const slotKey = insightTypeSlot[type];
  return slotKey ? domainPalette[themeKey][slotKey] : null;
};

// Theme-specific icon background color (hex, applied via inline style —
// Tailwind can't see dynamically-built class names at build time)
const getIconBgColor = (theme, type) => {
  const slot = getInsightSlot(theme, type);
  return slot ? slot.icon : '#9CA3AF';
};

// Card background + border colors based on insight type (applied via inline style)
const getCardBgColor = (theme, type) => {
  const slot = getInsightSlot(theme, type);
  return slot ? { background: slot.bg, borderColor: slot.border } : { background: '#F9FAFB', borderColor: '#E5E7EB' };
};

// Category label color (hex, applied via inline style) — same bright hue
// as the icon chip and progress bar, so a card only ever shows one hue
// instead of a muted variant plus a bright one
const getCategoryColor = (theme, type) => {
  const slot = getInsightSlot(theme, type);
  return slot ? (slot.ink || slot.icon) : '#6B7280';
};

// Badge colors — a simple positive/negative signal, separate from the domain
// hue above; only the warning color moved from amber to red
const getBadgeColor = (type, isPositive = true) => {
  if (type.startsWith('fallback')) {
    return 'bg-gray-100 text-gray-600';
  }
  if (type === 'savingsBehind' || type === 'spendingOver' || type === 'netWorthDown' || type === 'investmentReminder') {
    return 'bg-red-50 text-red-600';
  }
  if (type === 'netWorthUp' || isPositive) {
    return 'bg-emerald-100 text-emerald-700';
  }
  return 'bg-gray-100 text-gray-700';
};

// Progress bar color (hex, applied via inline style) — matches the icon
// chip above it rather than the muted text hex
const getProgressColor = (theme, type) => {
  const slot = getInsightSlot(theme, type);
  return slot ? (slot.bar || slot.icon) : '#9CA3AF';
};

const InfoIcon = () => (
  <span className="text-sm">ℹ️</span>
);

// Single Insight Card Component
const InsightCard = ({ insight, theme: themeProp, onAction, index }) => { // w2_11: index
  const theme = themeProp;
  const {
    type,
    category,
    title,
    description,
    value,
    badge,
    progress,
    progressLabel,
    progressPercent,
    icon,
    infoTooltip
  } = insight;

  const purple = usePurple();
  // plan_02: Money plan shows a small, flat pastel tile.
  const plan = usePlan();
  const iconBgColor = getIconBgColor(theme, type);
  // Purple: white cards, and a soft tint of the colour behind the emoji.
  const cardBgColor = purple ? { background: '#FFFFFF', borderColor: '#F3ECF7' } : getCardBgColor(theme, type);
  const iconSquareBg = purple ? '#F1E9FB' : iconBgColor;
  const categoryColor = getCategoryColor(theme, type);
  const labelColor = purple ? '#7B5AA6' : categoryColor;
  const dotColor = purple ? '#AC8DF2' : iconBgColor;
  const badgeColor = getBadgeColor(type, badge?.isPositive);
  const progressColor = getProgressColor(theme, type);

  // Get icon component
  const IconComponent = () => {
    switch (icon) {
      case 'pig': return <PigIcon />;
      case 'party': return <PartyIcon />;
      case 'hourglass': return <HourglassIcon />;
      case 'chartUp': return <ChartUpIcon />;
      case 'chartDown': return <ChartDownIcon />;
      case 'trophy': return <TrophyIcon />;
      case 'satellite': return <SatelliteIcon />;
      case 'lightbulb': return <LightbulbIcon />;
      case 'warning': return <WarningIcon />;
      case 'fire': return <FireIcon />;
      case 'shopping': return <ShoppingIcon />;
      case 'thinking': return <ThinkingIcon />;
      case 'clipboard': return <span className="text-2xl">📋</span>;
      default: return <PigIcon />;
    }
  };

  if (plan) {
    // plan_03: the whole insight, on a soft pastel tile.
    const tints = [
      { bg: '#DFF2E7', ink: '#2F8756', bar: '#9ED9B6' },
      { bg: '#ECE6FA', ink: '#7A5DD3', bar: '#B9A6F0' },
      { bg: '#FAE4ED', ink: '#C04B7C', bar: '#F2AFC8' },
      { bg: '#DFE9F9', ink: '#3C6DB5', bar: '#A9C6F0' },
    ];
    const key = String(type || '') + String(title || '');
    let h = 0;
    for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) % 997;
    // w2_11: take turns through the colours so neighbours never match
    const tint = tints[(Number.isInteger(index) ? index : h) % tints.length];
    return (
      <div
        className={`wy-insight wy-plan-insight flex-shrink-0 relative flex flex-col ${insight.action && onAction ? 'cursor-pointer' : ''}`}
        onClick={insight.action && onAction ? () => onAction(insight.action) : undefined}
        style={{ width: '252px', height: '214px', padding: '16px', borderRadius: '22px', background: tint.bg }}
        data-testid={`insight-card-${type}`}
      >
        <div className="flex items-center" style={{ gap: '10px', marginBottom: '10px', paddingRight: infoTooltip ? '20px' : 0 }}>
          <span style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconComponent />
          </span>
          <span className="truncate" style={{ fontSize: '12.5px', fontWeight: 600, color: tint.ink }}>{category ? String(category).charAt(0) + String(category).slice(1).toLowerCase() : ''}</span>
        </div>
        {infoTooltip && (
          <div className="absolute group" style={{ top: '16px', right: '14px' }}>
            <button className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(255,255,255,0.75)', color: '#6C6879' }}>i</button>
            <div className="absolute right-0 top-full mt-2 w-56 p-3 bg-white text-gray-700 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg leading-relaxed">
              {infoTooltip}
            </div>
          </div>
        )}
        <p className="line-clamp-2" style={{ margin: 0, fontSize: '15px', fontWeight: 600, lineHeight: 1.3, color: '#26252E' }}>{title}</p>
        <p className="line-clamp-2" style={{ margin: '4px 0 0', fontSize: '13px', lineHeight: 1.45, color: '#5E5A6B', flexGrow: 1 }}>{description}</p>
        <div className="flex items-center" style={{ gap: '8px', marginTop: '8px', minWidth: 0 }}>
          <span className="whitespace-nowrap" style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.5px', color: '#26252E' }}>{value}</span>
          {badge && (
            <span className="rounded-full whitespace-nowrap truncate" style={{ fontSize: '12px', fontWeight: 600, color: tint.ink, background: 'rgba(255,255,255,0.75)', padding: '3px 9px' }}>{badge.text}</span>
          )}
        </div>
        {progress !== undefined && (
          <div className="rounded-full overflow-hidden" style={{ height: '6px', background: 'rgba(255,255,255,0.75)', marginTop: '10px' }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(progress, 100)}%`, background: tint.bar }} />
          </div>
        )}
      </div>
    );
  }

  // Purple: a smaller card (Option A). The emoji sits beside the category
  // and title, the description is cut to two lines.
  if (purple) {
    return (
      <div
        className={`wy-insight flex-shrink-0 rounded-2xl border flex flex-col relative ${insight.action && onAction ? 'cursor-pointer' : ''}`}
        onClick={insight.action && onAction ? () => onAction(insight.action) : undefined}
        style={{ width: '228px', height: '176px', padding: '14px', background: '#FFFFFF', borderColor: '#F3ECF7', boxShadow: '0 6px 22px rgba(120,60,150,0.09)' }}
        data-testid={`insight-card-${type}`}
      >
        <div className="flex items-center" style={{ gap: '10px', marginBottom: '8px', paddingRight: infoTooltip ? '18px' : 0 }}>
          <div className="rounded-xl flex items-center justify-center flex-shrink-0 wy-insight-em" style={{ width: '36px', height: '36px', background: '#F1E9FB' }}>
            <IconComponent />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#AC8DF2' }} />
              <span className="text-[10px] font-bold uppercase tracking-wider truncate" style={{ color: '#7B5AA6' }}>{category}</span>
            </div>
            <h3 className="font-bold text-gray-800 line-clamp-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px', lineHeight: 1.25 }}>{title}</h3>
          </div>
        </div>
        {infoTooltip && (
          <div className="absolute top-3 right-3 group">
            <button className="w-5 h-5 rounded-full bg-gray-200/60 text-gray-500 flex items-center justify-center text-xs font-bold">i</button>
            <div className="absolute right-0 top-full mt-2 w-56 p-3 bg-white text-gray-700 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg border border-gray-200 leading-relaxed">
              {infoTooltip}
            </div>
          </div>
        )}
        <p className="text-gray-500 line-clamp-2" style={{ fontSize: '12px', lineHeight: 1.45, flexGrow: 1 }}>{description}</p>
        <div className="flex items-center gap-2" style={{ marginTop: '6px' }}>
          <span className="whitespace-nowrap" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', color: '#5E35A8', lineHeight: 1.1 }}>{value}</span>
          {badge && (
            <span className="rounded-full whitespace-nowrap truncate" style={{ fontSize: '11.5px', fontWeight: 700, color: '#5E35A8', background: '#F1E9FB', padding: '3px 9px' }}>{badge.text}</span>
          )}
        </div>
        {progress !== undefined && (
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#EEE7F3', marginTop: '8px' }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(progress, 100)}%`, background: 'linear-gradient(90deg, #D6388A 0%, #8B2FE0 100%)' }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`wy-insight flex-shrink-0 w-[280px] h-[260px] rounded-2xl border p-4 flex flex-col relative ${insight.action && onAction ? 'cursor-pointer' : ''}`}
      onClick={insight.action && onAction ? () => onAction(insight.action) : undefined}
      style={{ background: cardBgColor.background, borderColor: cardBgColor.borderColor, boxShadow: purple ? '0 6px 22px rgba(120,60,150,0.09)' : undefined }}
      data-testid={`insight-card-${type}`}
    >
      {/* Info Tooltip in top right corner */}
      {infoTooltip && (
        <div className="absolute top-3 right-3 group">
          <button className="w-5 h-5 rounded-full bg-gray-200/60 text-gray-500 hover:bg-gray-200 flex items-center justify-center text-xs font-bold">
            i
          </button>
          <div className="absolute right-0 top-full mt-2 w-56 p-3 bg-white text-gray-700 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg border border-gray-200 leading-relaxed">
            {infoTooltip}
          </div>
        </div>
      )}

      {/* Icon */}
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: iconSquareBg }}>
        <IconComponent />
      </div>

      {/* Category Label */}
      <div className="flex items-center gap-1.5 mb-1">
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
        <span 
          className="text-[10px] font-bold uppercase tracking-wider"
          style={{ color: labelColor }}
        >
          {category}
        </span>
      </div>

      {/* Title */}
      <h3 
        className="text-base font-bold text-gray-800 mb-1 line-clamp-1"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {title}
      </h3>

      {/* Description */}
      <p className="text-xs text-gray-500 mb-3 leading-relaxed flex-grow">
        {description}
      </p>

      {/* Value and Badge */}
      <div className="flex items-center gap-2 mb-2">
        <span 
          className="text-2xl font-bold text-gray-800"
          style={{ fontFamily: "'DM Serif Display', serif" }}
        >
          {value}
        </span>
        {badge && (
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badgeColor}`}>
            {badge.text}
          </span>
        )}
      </div>

      {/* Progress Bar (optional) */}
      {progress !== undefined && (
        <div className="mt-auto">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-1" style={purple ? { background: '#EEE7F3' } : undefined}>
            <div 
              className="h-full rounded-full"
              style={{ width: `${Math.min(progress, 100)}%`, background: purple ? 'linear-gradient(90deg, #D6388A 0%, #8B2FE0 100%)' : progressColor }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-400">
            <span>{progressLabel}</span>
            <span>{progressPercent}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Main SpendingInsights Component
const SpendingInsights = ({
  theme = 'pink',
  sinkingFunds = [],
  investmentAccounts = [],
  transactions = [],
  budgetSettings = {},
  categoryBudgets = {},
  noSpendDays = {},
  wishlistItems = [],
  netWorth = 0,
  previousNetWorth = 0,
  netWorthBreakdown = {},
  monthlyTargets = {},
  onSeeAll,
  demoMode = false,
  maxInsights,
  lockedTeasers = [],
  onUnlockClick,
  showDemoToggle = false,
  onToggleDemoMode,
  onAction,
  leadingInsights = []
}) => {
  const scrollRef = useRef(null);
  const purpleRow = usePurple();
  const [showAllRows, setShowAllRows] = useState(false);
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  // Current month calculations
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentDay = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const monthProgress = Math.round((currentDay / daysInMonth) * 100);
  const monthName = now.toLocaleDateString('en-US', { month: 'long' });

  // Demo mode insights - shows all insight types with sample data
  const demoInsights = [
    {
      type: 'savingsGoal',
      category: 'SAVINGS',
      title: 'Savings goal complete!',
      description: 'You hit your $500 savings goal this month!',
      value: '$500',
      badge: { text: '100% ✓', isPositive: true },
      progress: 100,
      progressLabel: 'Goal complete!',
      progressPercent: '🎉',
      icon: 'party',
      infoTooltip: 'Total deposits to sinking funds this month ($500) compared to your combined monthly savings budget ($500).'
    },
    {
      type: 'savings',
      category: 'SAVINGS',
      title: 'Savings on track!',
      description: '$320 saved — 64% of your $500 monthly goal.',
      value: '$320',
      badge: { text: '↑ 64%', isPositive: true },
      progress: 64,
      progressLabel: 'of $500 goal',
      progressPercent: '64%',
      icon: 'pig',
      infoTooltip: "You're 64% toward your savings goal, and we're 75% through the month. You're on pace!"
    },
    {
      type: 'savingsBehind',
      category: 'SAVINGS',
      title: 'Behind on savings',
      description: 'Only $150 saved of $500 goal. 75% of month passed.',
      value: '$150',
      badge: { text: '30% only', isPositive: false },
      progress: 30,
      progressLabel: 'of $500 goal',
      progressPercent: '30%',
      icon: 'hourglass',
      infoTooltip: "You're 30% toward your savings goal, but 75% of the month has passed. Consider adding more to catch up."
    },
    {
      type: 'savingsGoalFund',
      category: 'SAVINGS',
      title: 'Vacation Fund complete!',
      description: 'You hit your $200 Vacation Fund goal this month!',
      value: '$200',
      badge: { text: '100% ✓', isPositive: true },
      progress: 100,
      progressLabel: 'Goal complete!',
      progressPercent: '🎉',
      icon: 'party',
      infoTooltip: 'Deposits to Vacation Fund this month vs your monthly budget for this fund.'
    },
    {
      type: 'netWorthUp',
      category: 'NET WORTH',
      title: 'Net worth up +$1,250!',
      description: 'From $23,500 → $24,750. Great progress!',
      value: '$24,750',
      badge: { text: '↑ +$1,250', isPositive: true },
      icon: 'chartUp',
      infoTooltip: 'Comparing your total assets minus debts this month vs last month.'
    },
    {
      type: 'netWorthDown',
      category: 'NET WORTH',
      title: 'Net worth dipped -$800',
      description: 'Down from $25,000 last month. High spending may be the cause.',
      value: '$800',
      badge: { text: '↓ this month', isPositive: false },
      icon: 'chartDown',
      infoTooltip: 'Comparing your total assets minus debts this month vs last month.'
    },
    {
      type: 'milestone',
      category: 'NET WORTH',
      title: '$50k milestone ahead!',
      description: "At this rate you'll hit $50k net worth in 8 months.",
      value: '8 mo',
      badge: { text: 'on track', isPositive: true },
      progress: 72,
      progressLabel: '$36,000 of $50k',
      progressPercent: '72%',
      icon: 'trophy',
      infoTooltip: 'Based on your $1,750 net worth increase this month. Milestones: $10k, $25k, $50k, $100k, etc.'
    },
    {
      type: 'investmentReminder',
      category: 'INVESTMENTS',
      title: 'No deposit this month',
      description: "You haven't added to your TFSA yet in March. Don't forget!",
      value: '$0',
      badge: { text: 'this month', isPositive: false },
      icon: 'lightbulb',
      infoTooltip: "I check if you've made any deposits to TFSA this month. This reminder shows after the 7th."
    },
    {
      type: 'investment',
      category: 'INVESTMENTS',
      title: 'RRSP at 45% of goal',
      description: '$9,000 toward $20,000 target.',
      value: '$9,000',
      badge: { text: '45%', isPositive: false },
      progress: 45,
      progressLabel: 'of $20,000',
      icon: 'satellite',
      infoTooltip: 'Starting balance + deposits - withdrawals + interest = current balance. Compared to your $20,000 goal.'
    },
    {
      type: 'spendingOver',
      category: 'SPENDING',
      title: 'Dining out over budget',
      description: '$380 spent vs $250 budget — $130 over with 8 days left.',
      value: '152%',
      badge: { text: '$130 over', isPositive: false },
      progress: 100,
      progressLabel: '$380 of $250',
      progressPercent: 'Over!',
      icon: 'warning',
      infoTooltip: 'Total Dining out spending this month compared to your category budget.'
    },
    {
      type: 'spendingUnder',
      category: 'SPENDING',
      title: 'Great groceries month!',
      description: 'Only $280 of $450 budget used. $170 left!',
      value: '$170',
      badge: { text: 'left', isPositive: true },
      progress: 62,
      progressLabel: '$280 of $450',
      progressPercent: '62%',
      icon: 'party',
      infoTooltip: "Shows when you're under 70% of your category budget with less than 20% of the month left. Great discipline!"
    },
    {
      type: 'calendar',
      category: 'CALENDAR',
      title: '12 no-spend days!',
      description: "You've had 12 no-spend days this month.",
      value: '12 days',
      badge: { text: '~$420 saved', isPositive: true },
      icon: 'fire',
      infoTooltip: 'Estimated savings are your no-spend days × your average daily spending.'
    },
    {
      type: 'wishlist',
      category: 'WISHLIST',
      title: 'You can afford this!',
      description: 'AirPods Pro ($329) fits in your unbudgeted money.',
      value: '$329',
      badge: { text: 'affordable', isPositive: true },
      icon: 'shopping',
      infoTooltip: 'You have $450 left to budget this month — money not assigned to any category or savings goal.'
    },
    {
      type: 'fallbackSavings',
      category: 'TIP',
      title: 'Start a sinking fund',
      description: 'Set up savings goals to track progress toward big purchases or emergencies.',
      value: 'Get started',
      badge: { text: 'tip', isPositive: true },
      icon: 'pig',
      infoTooltip: 'Sinking funds help you save for planned expenses like vacations, gifts, or car repairs.'
    },
    {
      type: 'fallbackBudget',
      category: 'TIP',
      title: 'Set category budgets',
      description: 'Track spending by category to see where your money goes each month.',
      value: 'Budget tab',
      badge: { text: 'tip', isPositive: true },
      icon: 'warning',
      infoTooltip: 'Category budgets help you control spending in areas like groceries, entertainment, and dining.'
    }
  ];

  // Generate insights based on user data
  const insights = useMemo(() => {
    // Return demo insights if in demo mode
    if (demoMode) {
      return demoInsights;
    }

    const insightsList = [];

    // Get current month transactions
    const monthTransactions = transactions.filter(t => t.month_key === currentMonth);
    
    // Calculate savings deposits this month (sinking funds only, not investments)
    const savingsDeposits = monthTransactions
      .filter(t => t.is_savings && t.account_type === 'fund' && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    // Calculate total savings budget for the month (sinking funds only)
    const monthSavingsBudgets = budgetSettings.savings_budgets_by_month?.[currentMonth] || {};
    const sinkingFundIds = new Set(sinkingFunds.map(f => f.id));
    const totalSavingsBudget = Object.entries(monthSavingsBudgets)
      .filter(([id]) => sinkingFundIds.has(id))
      .reduce((sum, [, b]) => sum + Math.max(0, b || 0), 0);

    // Track insights per category (allow max 2 per category)
    const categoryCount = {};
    const MAX_PER_CATEGORY = 2;

    // Helper to add insight if category hasn't reached max
    const addInsight = (insight) => {
      const count = categoryCount[insight.category] || 0;
      if (count < MAX_PER_CATEGORY) {
        // Check for duplicate type
        const hasDuplicate = insightsList.some(i => i.type === insight.type);
        if (!hasDuplicate) {
          insightsList.push(insight);
          categoryCount[insight.category] = count + 1;
        }
      }
    };

    // 1. SAVINGS INSIGHTS
    if (totalSavingsBudget > 0) {
      const savingsPercent = Math.round((savingsDeposits / totalSavingsBudget) * 100);
      
      if (savingsPercent >= 100) {
        addInsight({
          type: 'savingsGoal',
          category: 'SAVINGS',
          title: 'Savings goal complete!',
          description: `You hit your ${formatCurrency(totalSavingsBudget)} savings goal this month!`,
          value: formatCurrency(savingsDeposits),
          badge: { text: '100% ✓', isPositive: true },
          progress: 100,
          progressLabel: 'Goal complete!',
          progressPercent: '🎉',
          icon: 'party',
          infoTooltip: `Total deposits to sinking funds this month (${formatCurrency(savingsDeposits)}) compared to your combined monthly savings budget (${formatCurrency(totalSavingsBudget)}).`
        });
      } else if (savingsPercent >= monthProgress - 10 && currentDay > 5) {
        addInsight({
          type: 'savings',
          category: 'SAVINGS',
          title: 'Savings on track!',
          description: `${formatCurrency(savingsDeposits)} saved — ${savingsPercent}% of your ${formatCurrency(totalSavingsBudget)} monthly goal.`,
          value: formatCurrency(savingsDeposits),
          badge: { text: `↑ ${savingsPercent}%`, isPositive: true },
          progress: savingsPercent,
          progressLabel: `of ${formatCurrency(totalSavingsBudget)} goal`,
          progressPercent: `${savingsPercent}%`,
          icon: 'pig',
          infoTooltip: `You're ${savingsPercent}% toward your savings goal, and we're ${monthProgress}% through the month. You're on pace!`
        });
      } else if (savingsPercent < monthProgress - 10 && savingsDeposits > 0 && currentDay > 5) {
        addInsight({
          type: 'savingsBehind',
          category: 'SAVINGS',
          title: 'Behind on savings',
          description: `Only ${formatCurrency(savingsDeposits)} saved of ${formatCurrency(totalSavingsBudget)} goal. ${monthProgress}% of month passed.`,
          value: formatCurrency(savingsDeposits),
          badge: { text: `${savingsPercent}% only`, isPositive: false },
          progress: savingsPercent,
          progressLabel: `of ${formatCurrency(totalSavingsBudget)} goal`,
          progressPercent: `${savingsPercent}%`,
          icon: 'hourglass',
          infoTooltip: `You're ${savingsPercent}% toward your savings goal, but ${monthProgress}% of the month has passed. Consider adding more to catch up.`
        });
      }
    }

    // 2. Individual sinking fund goals
    for (const fund of sinkingFunds) {
      if ((categoryCount['SAVINGS'] || 0) >= MAX_PER_CATEGORY) break;
      
      const fundBudget = (budgetSettings.savings_budgets_by_month?.[currentMonth] || {})[fund.id] || 0;
      if (fundBudget <= 0) continue;
      
      const fundDeposits = monthTransactions
        .filter(t => t.is_savings && t.account_type === 'fund' && t.account_id === fund.id && t.transaction_type === 'deposit')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const fundPercent = Math.round((fundDeposits / fundBudget) * 100);
      
      if (fundPercent >= 100) {
        addInsight({
          type: 'savingsGoalFund',
          category: 'SAVINGS',
          title: `${fund.name} complete!`,
          description: `You hit your ${formatCurrency(fundBudget)} ${fund.name} goal this month!`,
          value: formatCurrency(fundDeposits),
          badge: { text: '100% ✓', isPositive: true },
          progress: 100,
          progressLabel: 'Goal complete!',
          progressPercent: '🎉',
          icon: 'party',
          infoTooltip: `Deposits to ${fund.name} this month vs your monthly budget for this fund.`
        });
      }
    }

    // 3. NET WORTH INSIGHTS
    // Only show if user has completed at least one full month (not comparing onboarding data)
    const previousMonths = Object.keys(budgetSettings.budget_by_month || {}).filter(m => m < currentMonth);
    const hasCompletedAMonth = previousMonths.length > 0;
    const netWorthChange = netWorth - previousNetWorth;
    
    if (hasCompletedAMonth && previousNetWorth !== 0 && netWorthChange > 0) {
      addInsight({
        type: 'netWorthUp',
        category: 'NET WORTH',
        title: `Net worth up +${formatCurrency(netWorthChange)}!`,
        description: `From ${formatCurrency(previousNetWorth)} → ${formatCurrency(netWorth)}. Great progress!`,
        value: formatCurrency(netWorth),
        badge: { text: `↑ +${formatCurrency(netWorthChange)}`, isPositive: true },
        icon: 'chartUp',
        infoTooltip: `Comparing your total assets minus debts this month vs last month.`
      });
    } else if (hasCompletedAMonth && previousNetWorth !== 0 && netWorthChange < 0) {
      // Calculate what changed most
      const b = netWorthBreakdown;
      const changes = [
        { name: 'savings', diff: (b.savings || 0) - (b.prevSavings || 0) },
        { name: 'investments', diff: (b.investments || 0) - (b.prevInvestments || 0) },
        { name: 'assets', diff: (b.assets || 0) - (b.prevAssets || 0) },
        { name: 'cash available', diff: (b.cash || 0) - (b.prevCash || 0) },
        { name: 'debts', diff: (b.prevDebts || 0) - (b.debts || 0) },
      ].filter(c => c.diff < 0).sort((a, b) => a.diff - b.diff);
      const dipCause = changes.length > 0
        ? `Mainly from a ${formatCurrency(Math.abs(changes[0].diff))} drop in ${changes[0].name}.`
        : `Down from ${formatCurrency(previousNetWorth)} last month.`;
      addInsight({
        type: 'netWorthDown',
        category: 'NET WORTH',
        title: `Net worth dipped ${formatCurrency(netWorthChange)}`,
        description: dipCause,
        value: formatCurrency(Math.abs(netWorthChange)),
        badge: { text: '↓ this month', isPositive: false },
        icon: 'chartDown',
        infoTooltip: `Comparing your total assets minus debts this month vs last month.`
      });
    }

    // 4. MILESTONE INSIGHT
    const milestones = [10000, 25000, 50000, 100000, 250000, 500000, 1000000];
    const nextMilestone = milestones.find(m => m > netWorth);
    if (hasCompletedAMonth && nextMilestone && netWorthChange > 0 && (categoryCount['NET WORTH'] || 0) < MAX_PER_CATEGORY) {
      const monthsToMilestone = Math.ceil((nextMilestone - netWorth) / netWorthChange);
      if (monthsToMilestone <= 24 && monthsToMilestone > 0) {
        const milestonePercent = Math.round((netWorth / nextMilestone) * 100);
        addInsight({
          type: 'milestone',
          category: 'NET WORTH',
          title: `${getCurrentCurrency().symbol}${(nextMilestone / 1000)}k milestone ahead!`,
          description: `At this rate you'll hit ${getCurrentCurrency().symbol}${(nextMilestone / 1000)}k net worth in ${monthsToMilestone} months.`,
          value: `${monthsToMilestone} mo`,
          badge: { text: 'on track', isPositive: true },
          progress: milestonePercent,
          progressLabel: `${formatCurrency(netWorth)} of ${getCurrentCurrency().symbol}${(nextMilestone / 1000)}k`,
          progressPercent: `${milestonePercent}%`,
          icon: 'trophy',
          infoTooltip: `Based on your ${formatCurrency(netWorthChange)} net worth increase this month. Milestones: $10k, $25k, $50k, $100k, etc.`
        });
      }
    }

    // 5. INVESTMENT INSIGHTS — rotate featured account by day of month
    if (investmentAccounts.length > 0) {
      const inv = investmentAccounts[currentDay % investmentAccounts.length];

      const invDeposits = monthTransactions
        .filter(t => t.is_savings && t.account_type === 'investment' && t.account_id === inv.id && t.transaction_type === 'deposit')
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      // No deposit reminder
      if (invDeposits === 0 && currentDay > 7 && (categoryCount['INVESTMENTS'] || 0) < MAX_PER_CATEGORY) {
        addInsight({
          type: 'investmentReminder',
          category: 'INVESTMENTS',
          title: 'No deposit this month',
          description: `You haven't added to your ${inv.name} yet in ${monthName}. Don't forget!`,
          value: '$0',
          badge: { text: 'this month', isPositive: false },
          icon: 'lightbulb',
          infoTooltip: `I check if you've made any deposits to ${inv.name} this month. This reminder shows after the 7th.`
        });
      }

      // Investment progress
      if (inv.goal && inv.goal > 0 && (categoryCount['INVESTMENTS'] || 0) < MAX_PER_CATEGORY) {
        const currentBalance = investmentBalance(inv, transactions, currentMonth, { all: true }); // dark_13

        const invPercent = Math.round((currentBalance / inv.goal) * 100);
        if (invPercent < 100 && invPercent > 0) {
          addInsight({
            type: 'investment',
            category: 'INVESTMENTS',
            title: `${inv.name} at ${invPercent}% of goal`,
            description: `${formatCurrency(currentBalance)} toward ${formatCurrency(inv.goal)} target.`,
            value: formatCurrency(currentBalance),
            badge: { text: `${invPercent}%`, isPositive: invPercent >= 50 },
            progress: invPercent,
            progressLabel: `of ${formatCurrency(inv.goal)}`,
            icon: 'satellite',
            infoTooltip: `Starting balance + deposits - withdrawals + interest = current balance. Compared to your ${formatCurrency(inv.goal)} goal.`
          });
        }
      }
    }

    // 6. SPENDING INSIGHTS
    const categories = budgetSettings.categories || [];
    const currentMonthCategoryBudgets = categoryBudgets[currentMonth] || {};
    const spendingByCategory = {};
    
    monthTransactions
      .filter(t => !t.is_income && !t.is_savings && !t.is_transfer && !t.is_debt_payment)
      .forEach(t => {
        const cat = t.category || 'Other';
        spendingByCategory[cat] = (spendingByCategory[cat] || 0) + (t.amount || 0);
      });

    // Over budget
    for (const cat of categories) {
      if ((categoryCount['SPENDING'] || 0) >= MAX_PER_CATEGORY) break;
      
      const budget = currentMonthCategoryBudgets[cat] || 0;
      if (budget <= 0) continue;
      
      const spent = spendingByCategory[cat] || 0;
      const spentPercent = Math.round((spent / budget) * 100);
      const daysLeft = daysInMonth - currentDay;
      
      if (spentPercent > 100) {
        const overAmount = spent - budget;
        addInsight({
          type: 'spendingOver',
          category: 'SPENDING',
          title: `${cat} over budget`,
          description: `${formatCurrency(spent)} spent vs ${formatCurrency(budget)} budget — ${formatCurrency(overAmount)} over with ${daysLeft} days left.`,
          value: `${spentPercent}%`,
          badge: { text: `${formatCurrency(overAmount)} over`, isPositive: false },
          progress: Math.min(spentPercent, 100),
          progressLabel: `${formatCurrency(spent)} of ${formatCurrency(budget)}`,
          progressPercent: 'Over!',
          icon: 'warning',
          infoTooltip: `Total ${cat} spending this month compared to your category budget.`
        });
      }
    }
    
    // Under budget
    if (monthProgress > 80) {
      for (const cat of categories) {
        if ((categoryCount['SPENDING'] || 0) >= MAX_PER_CATEGORY) break;
        
        const budget = currentMonthCategoryBudgets[cat] || 0;
        if (budget <= 0) continue;
        
        const spent = spendingByCategory[cat] || 0;
        const spentPercent = Math.round((spent / budget) * 100);
        
        if (spentPercent < 70 && spentPercent > 0) {
          const savedAmount = budget - spent;
          addInsight({
            type: 'spendingUnder',
            category: 'SPENDING',
            title: `Great ${cat.toLowerCase()} month!`,
            description: `Only ${formatCurrency(spent)} of ${formatCurrency(budget)} budget used. ${formatCurrency(savedAmount)} left!`,
            value: formatCurrency(savedAmount),
            badge: { text: 'left', isPositive: true },
            progress: spentPercent,
            progressLabel: `${formatCurrency(spent)} of ${formatCurrency(budget)}`,
            progressPercent: `${spentPercent}%`,
            icon: 'party',
            infoTooltip: `Shows when you're under 70% of your category budget with less than 20% of the month left. Great discipline!`
          });
        }
      }
    }

    // 7. CALENDAR - NO SPEND STREAK
    const currentMonthNoSpendDays = (noSpendDays[currentMonth] || []).length;
    
    const monthTarget = monthlyTargets?.[currentMonth] || 15;
    
    // Calculate average daily spending from user's data
    const totalMonthExpenses = monthTransactions
      .filter(t => !t.is_income && !t.is_savings && !t.is_transfer && !t.is_debt_payment)
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    const spendDays = currentDay - currentMonthNoSpendDays;
    const avgDailySpending = spendDays > 0 ? totalMonthExpenses / spendDays : 0;

    // Use budget-per-day for a stable, meaningful savings estimate
    const noSpendMonthlyBudget = budgetSettings.budget_by_month?.[currentMonth] || 0;
    const budgetPerDay = noSpendMonthlyBudget > 0 ? noSpendMonthlyBudget / daysInMonth : avgDailySpending;
    
    if (currentMonthNoSpendDays > 0) {
      const estimatedSavings = currentMonthNoSpendDays * budgetPerDay;
      
      addInsight({
        type: 'calendar',
        category: 'CALENDAR',
        title: `${currentMonthNoSpendDays} no-spend days!`,
        description: `You've had ${currentMonthNoSpendDays} no-spend days this month.`,
        value: `${currentMonthNoSpendDays} days`,
        badge: { text: `~${formatCurrency(estimatedSavings)} saved`, isPositive: true },
        icon: 'fire',
        infoTooltip: `Estimated savings are your no-spend days × your daily budget (your total monthly budget ÷ days in the month).`
      });
    }

    // 8a. THINK IT OVER - the wishlist item waiting longest without a verdict.
    // Goes away by itself once every item has been through the quiz.
    const unthought = wishlistItems
      .filter((i) => i && !i.purchased && !i.quiz_result)
      .sort((a, b) => String(a.date_added || '').localeCompare(String(b.date_added || '')));
    if (unthought.length > 0) {
      const item = unthought[0];
      const days = item.date_added
        ? Math.max(0, Math.floor((Date.now() - new Date(item.date_added).getTime()) / 86400000))
        : null;
      const name = cleanItemName(item.name);
      const since = days === null ? '' : days === 0 ? ' since today' : ` for ${days} day${days === 1 ? '' : 's'}`;
      addInsight({
        type: 'thinkItOver',
        category: 'WISHLIST',
        title: 'Still want this?',
        description: `${name ? '\u201C' + name + '\u201D' : 'Something'} has been on your wishlist${since}. Take the quiz before you buy.`,
        value: formatCurrency(Number(item.value) || 0),
        badge: days === null ? null : { text: days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`, isPositive: false },
        icon: 'thinking',
        action: 'wishlist',
      });
    }

    // 8. WISHLIST
    // Calculate left to budget (unallocated money)
    const monthlyBudget = budgetSettings.budget_by_month?.[currentMonth] || 0;
    const totalCategoryBudgets = Object.values(currentMonthCategoryBudgets).reduce((sum, b) => sum + (b || 0), 0);
    const positiveSavingsBudgets = sinkingFunds
      .filter(f => (f.monthly_budget || 0) > 0)
      .reduce((sum, f) => sum + (f.monthly_budget || 0), 0);
    const totalDebtBudgets = Object.values(budgetSettings.debt_budgets_by_month?.[currentMonth] || {}).reduce((sum, b) => sum + (b || 0), 0);
    const plannedWithdrawals = sinkingFunds
      .filter(f => (f.monthly_budget || 0) < 0)
      .reduce((sum, f) => sum + Math.abs(f.monthly_budget || 0), 0);
    const leftToBudget = (monthlyBudget + plannedWithdrawals) - totalCategoryBudgets - positiveSavingsBudgets - totalDebtBudgets;

    for (const item of wishlistItems) {
      if ((categoryCount['WISHLIST'] || 0) >= MAX_PER_CATEGORY) break;
      
      if (item.price && item.price <= leftToBudget && item.price > 0 && leftToBudget > 0) {
        addInsight({
          type: 'wishlist',
          category: 'WISHLIST',
          title: 'You can afford this!',
          description: `${item.name} (${formatCurrency(item.price)}) fits in your unbudgeted money.`,
          value: formatCurrency(item.price),
          badge: { text: 'affordable', isPositive: true },
          icon: 'shopping',
          infoTooltip: `You have ${formatCurrency(leftToBudget)} left to budget this month — money not assigned to any category or savings goal.`
        });
      }
    }

    // 9. FALLBACK INSIGHTS
    const fallbackInsights = [];
    
    if (!categoryCount['SAVINGS'] && sinkingFunds.length === 0) {
      fallbackInsights.push({
        type: 'fallbackSavings',
        category: 'TIP',
        title: 'Start a sinking fund',
        description: 'Set up savings goals to track progress toward big purchases or emergencies.',
        value: 'Get started',
        badge: { text: 'tip', isPositive: true },
        icon: 'pig',
        infoTooltip: 'Sinking funds help you save for planned expenses like vacations, gifts, or car repairs.'
      });
    }
    
    if (!categoryCount['SPENDING'] && Object.keys(currentMonthCategoryBudgets).length === 0) {
      fallbackInsights.push({
        type: 'fallbackBudget',
        category: 'TIP',
        title: 'Set category budgets',
        description: 'Track spending by category to see where your money goes each month.',
        value: 'Budget tab',
        badge: { text: 'tip', isPositive: true },
        icon: 'warning',
        infoTooltip: 'Category budgets help you control spending in areas like groceries, entertainment, and dining.'
      });
    }
    
    if (!categoryCount['CALENDAR'] && currentMonthNoSpendDays === 0) {
      fallbackInsights.push({
        type: 'fallbackCalendar',
        category: 'TIP',
        title: 'Try a no-spend day',
        description: 'Challenge yourself to not spend anything tomorrow. Small wins add up!',
        value: 'Calendar',
        badge: { text: 'challenge', isPositive: true },
        icon: 'fire'
      });
    }
    
    if (!categoryCount['WISHLIST'] && wishlistItems.length === 0) {
      fallbackInsights.push({
        type: 'fallbackWishlist',
        category: 'TIP',
        title: 'Think before you buy',
        description: 'Add things you want to your wishlist, then take the quiz before you buy them.',
        value: 'Wishlist',
        badge: { text: 'tip', isPositive: true },
        icon: 'shopping',
        action: 'wishlist'
      });
    }

    // Mix insights so same category isn't adjacent
    const mixedInsights = [];
    const insightsByCategory = {};
    
    insightsList.forEach(insight => {
      if (!insightsByCategory[insight.category]) {
        insightsByCategory[insight.category] = [];
      }
      insightsByCategory[insight.category].push(insight);
    });
    
    // Interleave by category first
    let hasMore = true;
    let round = 0;
    while (hasMore && mixedInsights.length < 12) {
      hasMore = false;
      for (const cat of Object.keys(insightsByCategory)) {
        if (insightsByCategory[cat].length > round) {
          mixedInsights.push(insightsByCategory[cat][round]);
          hasMore = true;
        }
      }
      round++;
    }
    
    // Sort to avoid same background colors being adjacent
    // Group by card background color type
    // Group by the colour slot each card is actually drawn in (the same
    // insightTypeSlot table that colours it), so "not adjacent" means what
    // it looks like. Tips have no slot and share the neutral grey.
    const getColorGroup = (insight) => insightTypeSlot[insight.type] || 'gray';
    
    // Reorder to prevent same colors being adjacent
    const reorderedInsights = [];
    const remaining = [...mixedInsights];
    let lastColor = null;
    
    while (remaining.length > 0) {
      // Find an insight with a different color than the last one
      let found = false;
      for (let i = 0; i < remaining.length; i++) {
        const color = getColorGroup(remaining[i]);
        if (color !== lastColor || remaining.length === 1) {
          reorderedInsights.push(remaining[i]);
          lastColor = color;
          remaining.splice(i, 1);
          found = true;
          break;
        }
      }
      // If all remaining have same color as last, just add the first one
      if (!found && remaining.length > 0) {
        reorderedInsights.push(remaining[0]);
        lastColor = getColorGroup(remaining[0]);
        remaining.splice(0, 1);
      }
    }
    
    // Add fallbacks at end (minimum 3 total insights if possible)
    const minInsights = 3;
    const neededFallbacks = Math.max(0, minInsights - reorderedInsights.length, 8 - reorderedInsights.length);
    const fallbacksToAdd = fallbackInsights.slice(0, neededFallbacks);
    
    return [...reorderedInsights, ...fallbacksToAdd].slice(0, 10);
  }, [
    sinkingFunds, investmentAccounts, transactions, budgetSettings, 
    categoryBudgets, noSpendDays, wishlistItems, netWorth, previousNetWorth,
    monthlyTargets, currentMonth, currentDay, daysInMonth, monthProgress, monthName,
    demoMode
  ]);

  // Cap real insights if maxInsights is set (e.g. 1 for free users)
  // Cards passed in from outside (e.g. contributions to confirm) go first.
  const allInsights = [...(leadingInsights || []), ...insights];
  const visibleInsights = typeof maxInsights === 'number'
    ? allInsights.slice(0, maxInsights)
    : allInsights;

  const hasTeasers = lockedTeasers && lockedTeasers.length > 0;

  // Don't render if there are no real insights AND no teasers to show
  if (visibleInsights.length === 0 && !hasTeasers) {
    return null;
  }

  // Theme-aware gradient for the unlock CTA bar
  const getLockGradient = () => {
    if (purpleRow) return '#AC8DF2';
    if (isWarm) return '#2D5A3D';
    if (isPink) return '#e879a0';
    return '#4361EE';
  };

  // Purple: one calm list card instead of the swipe row. Top three show,
  // "See all" opens the rest in place.
  if (purpleRow) {
    const rows = showAllRows ? visibleInsights : visibleInsights.slice(0, 3);
    const extra = visibleInsights.length - 3;
    const teaserRows = hasTeasers ? lockedTeasers.slice(0, 2) : [];
    const Row = ({ insight, locked, last }) => {
      const IconEl = INSIGHT_ICONS[insight.icon] || INSIGHT_ICONS.pig;
      const clickable = !locked && insight.action && onAction;
      const note = insight.badge?.text || insight.progressPercent || '';
      return (
        <div
          className={`flex items-center ${clickable || locked ? 'cursor-pointer' : ''}`}
          onClick={locked ? onUnlockClick : clickable ? () => onAction(insight.action) : undefined}
          style={{ gap: '12px', padding: '12px 14px', borderBottom: last ? 'none' : '1px solid #F3ECF7', ...(locked ? { filter: 'blur(5px)', opacity: 0.45, pointerEvents: onUnlockClick ? 'auto' : 'none' } : {}) }}
          data-testid={locked ? 'insight-teaser-row' : `insight-row-${insight.type}`}
        >
          <div className="rounded-xl flex items-center justify-center flex-shrink-0 wy-insight-em" style={{ width: '38px', height: '38px', background: '#F1E9FB' }}>
            {IconEl}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="truncate" style={{ fontSize: '14px', fontWeight: 700, color: '#1F2937', lineHeight: 1.3 }}>{insight.title}</p>
            <p className="truncate" style={{ fontSize: '12px', color: '#7D7590', marginTop: '1px', lineHeight: 1.35 }}>{insight.description}</p>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, maxWidth: '42%' }}>
            <p className="truncate" style={{ fontSize: '14.5px', fontWeight: 700, color: '#5E35A8', lineHeight: 1.3 }}>{insight.value}</p>
            {note && (
              <p className="truncate" style={{ fontSize: '11px', fontWeight: 600, color: insight.badge && insight.badge.isPositive === false ? '#B45D5D' : insight.badge?.isPositive ? '#2E9E7B' : '#7D7590', marginTop: '1px' }}>{note}</p>
            )}
          </div>
        </div>
      );
    };
    return (
      <div className="mb-5" data-testid="spending-insights-section">
        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-[13px] font-bold tracking-[0.08em] uppercase" style={{ color: '#7B5AA6' }}>Your insights</p>
          {extra > 0 && (
            <button
              onClick={() => setShowAllRows(!showAllRows)}
              className="text-xs font-semibold flex items-center gap-0.5"
              style={{ color: '#8B5CE0' }}
              data-testid="insights-see-all"
            >
              {showAllRows ? 'Show less' : `See all ${visibleInsights.length}`}
              <ChevronRight className="w-3.5 h-3.5" style={{ transform: showAllRows ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }} />
            </button>
          )}
        </div>
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: '#F3ECF7', boxShadow: '0 6px 22px rgba(120,60,150,0.09)' }}>
          {rows.map((insight, i) => (
            <Row key={`${insight.type}-${i}`} insight={insight} last={i === rows.length - 1 && teaserRows.length === 0} />
          ))}
          {teaserRows.map((t, i) => (
            <Row key={`teaser-${i}`} insight={t} locked last={i === teaserRows.length - 1} />
          ))}
        </div>
        {hasTeasers && onUnlockClick && (
          <button
            onClick={onUnlockClick}
            className="w-full mt-2 px-4 py-3 rounded-2xl flex items-center justify-center gap-2 text-[14px] font-bold"
            style={{ background: '#fff', color: '#7B5AA6', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', fontFamily: "'DM Sans', sans-serif" }}
            data-testid="insights-unlock-cta"
          >
            {'\uD83D\uDD12'} Unlock more insights with Premium
          </button>
        )}
        {/* Demo Mode toggle is left out in Purple (admin test control). */}
      </div>
    );
  }

  return (
    <div className="mb-5" data-testid="spending-insights-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-[13px] font-bold tracking-[0.08em] uppercase" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Your insights</p>
        {onSeeAll && (
          <button 
            onClick={onSeeAll}
            className={`text-xs font-semibold flex items-center gap-0.5 ${
              isWarm ? 'text-[#2D5A3D]' : isPink ? 'text-pink-500' : 'text-indigo-500'
            }`}
            data-testid="insights-see-all"
          >
            See all
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Scrollable Cards (real insights + teasers in the same row) */}
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
        style={{ 
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {visibleInsights.map((insight, index) => (
          <div 
            key={`${insight.type}-${index}`} 
            style={{ scrollSnapAlign: 'start' }}
          >
            <InsightCard insight={insight} theme={theme} onAction={onAction} index={index} />
          </div>
        ))}

        {/* Blurred teaser cards — real InsightCards wrapped with heavy blur.
            The "Unlock more insights with Premium" CTA bar below the scroll
            row provides the call-to-action. */}
        {hasTeasers && lockedTeasers.map((teaser, index) => (
          <div
            key={`teaser-${index}`}
            style={{ scrollSnapAlign: 'start' }}
            onClick={onUnlockClick}
            className="cursor-pointer"
            data-testid={`insight-teaser-${index}`}
          >
            <div className="opacity-30 pointer-events-none select-none" style={{ filter: 'blur(6px)' }}>
              <InsightCard insight={teaser} theme={theme} />
            </div>
          </div>
        ))}

        {/* Demo Mode toggle — admin-only test control, placed last in the
            scroll row so it's reachable by scrolling to the end and doesn't
            take up permanent space above the insights */}
        {showDemoToggle && onToggleDemoMode && (
          <div style={{ scrollSnapAlign: 'start' }}>
            <button
              onClick={onToggleDemoMode}
              className={`flex-shrink-0 ${purpleRow ? 'w-[228px] h-[176px]' : 'w-[280px] h-[260px]'} rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors`}
              data-testid="insights-demo-toggle"
            >
              <span className="text-2xl">🔧</span>
              <span className="text-sm font-semibold">
                {demoMode ? 'Demo Mode: ON' : 'Demo Mode: OFF'}
              </span>
              <span className="text-[11px] text-gray-400">Admin only · tap to toggle</span>
            </button>
          </div>
        )}
      </div>

      {/* Unlock CTA bar — shown only when there are teasers */}
      {hasTeasers && onUnlockClick && (
        <button
          onClick={onUnlockClick}
          className="w-full mt-2 px-4 py-3 rounded-2xl flex items-center justify-center gap-2 text-[14px] font-bold"
          style={{ 
            background: '#fff',
            color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            fontFamily: "'DM Sans', sans-serif"
          }}
          data-testid="insights-unlock-cta"
        >
          {'\uD83D\uDD12'} Unlock more insights with Premium
        </button>
      )}
    </div>
  );
};

export default SpendingInsights;
